import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';

import { editingStepIdSet, newStepSet } from 'reducers/customTunnel';
import { useDialog } from 'utils/hooks';
import { customTunnelSelectors } from 'reducers';
import { useDeleteCustomTunnelStepMutation } from 'api/main';
import { texts } from 'utils/languages/fr';

import UpdateCustomStep from './UpdateCustomStep';
import { ConfirmDialog } from 'components';
import { Box } from '@mui/material';
import * as icons from 'mdi-material-ui';

const iconSx = {
    fontSize: '22px',
    userSelect: 'none',
    cursor: 'pointer',
    ':hover': { color: 'primary.main' },
};

const TunnelStep = ({ tunnelStep, index }) => {
    const dispatch = useDispatch();

    const { ppId: editingStepId } = useSelector(customTunnelSelectors.getEditingStepId);
    const [deleteCustomTunnelStep, { isLoading }] = useDeleteCustomTunnelStepMutation();

    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();
    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();

    const handleEdit = useCallback(() => {
        dispatch(newStepSet(null));
        dispatch(editingStepIdSet({ ppId: tunnelStep?.id, pmId: tunnelStep?.pmId }));
        handleEditClose();
    }, [dispatch, handleEditClose, tunnelStep]);

    const handleConfirmEdit = useCallback(() => {
        if (editingStepId || editingStepId === 0) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingStepId, handleEditOpen, handleEdit]);

    const handleDelete = useCallback(() => {
        deleteCustomTunnelStep({ ppId: tunnelStep.id, pmId: tunnelStep.pmId });
    }, [deleteCustomTunnelStep, tunnelStep]);

    if (tunnelStep?.id && editingStepId === tunnelStep?.id) {
        return <UpdateCustomStep />;
    }

    return (
        <Draggable draggableId={`${tunnelStep.id}-${tunnelStep.pmId}`} index={index}>
            {(provided, snapshot) => (
                <Box
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    sx={theme => ({
                        ...provided.draggableProps.style,
                        border: `2px solid ${theme.palette.grey[500]}`,
                        borderRadius: '5px',
                        marginY: 1,
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: snapshot.isDragging ? '.75' : '1',
                    })}
                >
                    {tunnelStep.title}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <ConfirmDialog
                            open={deleteOpen}
                            handleConfirm={handleDelete}
                            handleClose={handleDeleteClose}
                            title={texts.DELETE_TUNNEL_STEP}
                            loading={isLoading}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DELETE_TUNNEL_STEP}
                        </ConfirmDialog>
                        <ConfirmDialog
                            open={confirmEditOpen}
                            handleConfirm={handleEdit}
                            handleClose={handleEditClose}
                            title={texts.CHANGES_NOT_CONFIRMED}
                            confirmText={texts.CONFIRMER}
                            cancelText={texts.CANCEL}
                        >
                            {texts.CONFIRM_DISCARD_CHANGES}
                        </ConfirmDialog>
                        <icons.Pencil sx={iconSx} onClick={handleConfirmEdit} />
                        <icons.Delete
                            sx={{
                                ...iconSx,
                                ':hover': {
                                    color: 'error.main',
                                },
                            }}
                            onClick={handleDeleteOpen}
                        />
                        <span {...provided.dragHandleProps} style={{ display: 'flex' }}>
                            <icons.Drag
                                sx={{
                                    ...iconSx,
                                    cursor: 'move',
                                    ':hover': { color: 'initial' },
                                }}
                            />
                        </span>
                    </Box>
                </Box>
            )}
        </Draggable>
    );
};

TunnelStep.propTypes = {
    tunnelStep: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
};

export default TunnelStep;
